import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  BoltRounded,
  CheckCircleRounded,
  ErrorRounded,
  InfoRounded,
  RadioButtonUncheckedRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { StatusChipProps } from './StatusChip.types';
import { StyledStatusChip } from './StatusChip.styles';

/**
 * StatusChip - Componente centralizado para mostrar estados
 *
 * Usa colores consistentes del theme y un estilo elevado moderno.
 *
 * @example
 * <StatusChip status="success" label="Completado" />
 * <StatusChip status="warning" label="Pendiente" />
 * <StatusChip status="error" label="Cancelado" />
 * <StatusChip status="info" label="En Proceso" />
 */
export const StatusChip: React.FC<StatusChipProps> = ({
  status,
  label,
  size = 'small',
  density = 'default',
  visualStyle = 'quiet',
  withIcon = false,
  withDot = false,
  ...props
}) => {
  const icon = useMemo(() => {
    if (withDot) {
      return (
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            display: 'inline-block',
          }}
        />
      );
    }
    if (!withIcon) return undefined;
    const icons = {
      success: <CheckCircleRounded fontSize="small" />,
      warning: <WarningAmberRounded fontSize="small" />,
      error: <ErrorRounded fontSize="small" />,
      info: <InfoRounded fontSize="small" />,
      default: <RadioButtonUncheckedRounded fontSize="small" />,
      action: <BoltRounded fontSize="small" />,
    };
    return icons[status] ?? icons.default;
  }, [status, withDot, withIcon]);

  return (
    <StyledStatusChip
      label={label}
      status={status}
      size={size}
      density={density}
      icon={icon}
      visualStyle={visualStyle}
      {...props}
    />
  );
};

export default StatusChip;
